import { useState, useCallback, useEffect, useRef } from 'react';
import type { SessionInfo, ThreadAcl, EventFrame, ChatEventPayload } from '../types/gateway';
import type { ChatMessage } from '../types/foreman';
import { gateway } from '../gateway/connection';
import { parseForemanResponse } from '../gateway/parser';
import { extractUserMessageMeta } from '../gateway/userMessageMeta';
import { verifyStamps } from '../gateway/attributionApi';

export interface UseSessionsProps {
  /** Active username — used to filter sessions and key the welcome thread. */
  username: string | null | undefined;
  /** ACL list for the active user (from useThreadAcl). Sessions not in this list are hidden. */
  acl: ThreadAcl[];
  /** ACL helpers used to register a new session and to seed the welcome thread. */
  createAcl: (sessionKey: string, opts?: { name?: string; sharedWith?: string[] | '*' }) => Promise<ThreadAcl>;
  /** True while the ACL list is still loading. We won't render sessions until ready to avoid flashing dev-cruft. */
  aclLoading: boolean;
}

export interface UseSessionsReturn {
  sessions: SessionInfo[];
  activeSessionId: string | null;
  /** Creates a thread (ACL row + local switch) and resolves to its session
   *  key so callers (e.g. the compose-guard's "Start my own thread") can
   *  target it immediately. Null only when no user is logged in. */
  createSession: (name?: string) => Promise<string | null>;
  /** Sets the single source of truth for which thread is open. Pass null to
   *  open no thread (used when the active thread is deleted and none remain). */
  switchSession: (id: string | null) => void;
  loadingHistory: boolean;
  chatHistory: ChatMessage[];
}

interface OCSession {
  key: string;
  createdAt?: string;
  updatedAt?: number | string;
  metadata?: Record<string, unknown>;
}

interface OCHistoryMessage {
  role: string;
  content: string | { type: string; text?: string }[];
  timestamp?: number;
}

export function useSessions({ username, acl, createAcl, aclLoading }: UseSessionsProps): UseSessionsReturn {
  const [allSessions, setAllSessions] = useState<SessionInfo[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const fetchedRef = useRef(false);
  const welcomeSeededRef = useRef<string | null>(null);
  // Latest ACL, readable from inside loadHistory without re-running the
  // history effect when the ACL refreshes (that would refetch chat.history).
  const aclRef = useRef<ThreadAcl[]>(acl);
  aclRef.current = acl;
  // Monotonic token per loadHistory call — lets the async stamp-verification
  // discard its result if the user switched threads while it was in flight.
  const loadSeqRef = useRef(0);

  // Fetch sessions when gateway connects
  useEffect(() => {
    const unsub = gateway.onStatusChange((status) => {
      if (status === 'connected' && !fetchedRef.current) {
        fetchedRef.current = true;
        fetchSessions();
      }
      if (status === 'disconnected') {
        fetchedRef.current = false;
      }
    });

    if (gateway.connected && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchSessions();
    }

    return unsub;
  }, []);

  const fetchSessions = async () => {
    try {
      const result = await gateway.request('sessions.list', {}) as { sessions?: OCSession[] };
      const ocSessions = result?.sessions || [];

      const mapped: SessionInfo[] = ocSessions
        .filter((s) => s.key && s.key !== 'system')
        .map((s) => {
          const shortKey = extractSessionKey(s.key);
          return {
            id: shortKey,
            name: formatSessionName(shortKey),
            createdAt: s.createdAt || new Date().toISOString(),
            lastMessageAt: s.updatedAt ? new Date(s.updatedAt).toISOString() : (s.createdAt || new Date().toISOString()),
            metadata: s.metadata,
          };
        });

      // Deduplicate by id
      const seen = new Set<string>();
      const deduped = mapped.filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });

      deduped.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

      setAllSessions(deduped);
    } catch (err) {
      console.error('[useSessions] Failed to fetch sessions:', err);
      setAllSessions([]);
    }
  };

  // Filter sessions against the user's ACL. Anything not in the ACL is hidden.
  // Synthetic welcome thread (welcome:{username}) is always shown for the active user
  // even if the gateway has no session for it yet — chat.send creates it on demand.
  const sessions: SessionInfo[] = (() => {
    if (aclLoading || !username) return [];
    const aclKeys = new Set(acl.map((a) => a.sessionKey));
    const visible = allSessions.filter((s) => aclKeys.has(s.id));

    // Inject any ACL rows that don't yet have a corresponding gateway session
    // (e.g. freshly-created welcome thread, freshly-created thread before first message).
    for (const a of acl) {
      if (visible.find((s) => s.id === a.sessionKey)) continue;
      visible.push({
        id: a.sessionKey,
        name: a.name || formatSessionName(a.sessionKey),
        createdAt: new Date(a.createdAt * 1000).toISOString(),
        lastMessageAt: new Date(a.updatedAt * 1000).toISOString(),
      });
    }

    visible.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
    return visible;
  })();

  // Seed welcome thread on first login (no ACL rows for this user).
  useEffect(() => {
    if (aclLoading || !username) return;
    if (welcomeSeededRef.current === username) return;
    welcomeSeededRef.current = username;

    if (acl.length === 0) {
      const welcomeKey = `welcome:${username}`;
      createAcl(welcomeKey, { name: 'Welcome' })
        .then(() => setActiveSessionId(welcomeKey))
        .catch((err) => console.error('[useSessions] welcome thread seed failed:', err));
    } else if (!activeSessionId) {
      // First load with existing ACLs — open the most recent.
      const sorted = [...acl].sort((a, b) => b.updatedAt - a.updatedAt);
      setActiveSessionId(sorted[0].sessionKey);
    }
  }, [aclLoading, acl, username, createAcl, activeSessionId]);

  // Load history when session changes
  useEffect(() => {
    if (!activeSessionId) return;
    loadHistory(activeSessionId);
  }, [activeSessionId]);

  // BUG C — debounced history refresh whenever a turn COMPLETES on the active
  // thread. Watchers of a shared thread they don't own never receive the other
  // participant's user messages over the socket (only Foreman's broadcast
  // delta/final events), so their view otherwise shows Foreman replies with no
  // prompts, out of position vs the real conversation. Re-pulling the
  // authoritative, fully-ordered history after each final turn self-heals it.
  useEffect(() => {
    if (!activeSessionId) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const unsub = gateway.onEvent((evt: EventFrame) => {
      if (evt.event !== 'chat') return;
      const payload = evt.payload as ChatEventPayload | undefined;
      if (!payload || payload.state !== 'final') return;
      // Event keys arrive as "agent:main:KEY" — shorten to match activeSessionId.
      const raw = payload.sessionKey || '';
      const short = raw.includes(':') ? raw.split(':').slice(2).join(':') || raw : raw;
      if (short !== activeSessionId) return;
      if (t) clearTimeout(t);
      t = setTimeout(() => loadHistory(activeSessionId), 400);
    });
    return () => {
      if (t) clearTimeout(t);
      unsub();
    };
  }, [activeSessionId]);

  const loadHistory = async (sessionKey: string) => {
    if (!gateway.connected) {
      // BUG C now refetches after EVERY turn, so a transient disconnect must
      // NEVER blank a good, in-view history. Keep the current history if it
      // already belongs to THIS session; only clear when we're switching to a
      // different session (so we don't strand another thread's messages on
      // screen). Mirrors backup-sessions.mjs's "empty<-nonempty" guard.
      setChatHistory((prev) =>
        prev.length > 0 && prev[0].sessionId === sessionKey ? prev : []
      );
      return;
    }

    setLoadingHistory(true);
    const loadSeq = ++loadSeqRef.current;
    try {
      const result = await gateway.request('chat.history', {
        sessionKey,
        limit: 100,
      }) as { messages?: OCHistoryMessage[] };

      const messages = result?.messages || [];
      // Stable React keys derived from message IDENTITY, not array index. The
      // Bug-C refetch pulls a rolling 100-row window each turn, so on threads
      // >100 messages the window slides and index-based keys (`hist-…-${i}`)
      // shift under every row — remounting the whole list (scroll jump, lost
      // hover/correction state). A content-identity key is stable across that
      // slide. `dupSeen` disambiguates genuinely identical rows deterministically
      // by their order of appearance, so duplicates keep distinct, stable keys.
      const dupSeen = new Map<string, number>();
      const mapped: ChatMessage[] = messages.map((m) => {
        const text = extractHistoryText(m);
        const isUser = m.role === 'user' || m.role === 'human';
        // User messages come back with the hidden protocol blocks we sent
        // ([ROLE_CONTEXT]/[SENDER]/[TEACH]) still embedded — strip them and
        // pull out the author identity for shared-thread attribution.
        const meta = isUser ? extractUserMessageMeta(text) : null;
        const identity = `${m.timestamp ?? 0}|${m.role}|${shortHash(text)}`;
        const dupIdx = dupSeen.get(identity) ?? 0;
        dupSeen.set(identity, dupIdx + 1);
        return {
          id: `hist-${sessionKey}-${identity}-${dupIdx}`,
          type: isUser ? 'user' : 'foreman',
          content: meta ? meta.cleanText : text,
          parsed: isUser ? undefined : parseForemanResponse(text),
          sender: isUser
            ? (meta?.sender && meta.sender.username !== username ? meta.sender.name : 'You')
            : 'Foreman',
          timestamp: m.timestamp ? new Date(m.timestamp).toISOString() : new Date().toISOString(),
          sessionId: sessionKey,
          senderInfo: meta?.sender,
          images: extractHistoryImages(m),
        };
      });

      // A newer loadHistory (e.g. a fast thread switch during Bug-C's per-turn
      // refetch) supersedes this one — discard the stale response so thread A's
      // history can never land under thread B (the connected-path leak the
      // disconnect guard alone didn't cover).
      if (loadSeqRef.current !== loadSeq) return;
      setChatHistory(mapped);
      verifyHistoryStamps(sessionKey, mapped, loadSeq);
    } catch (err) {
      console.error('[useSessions] Failed to load history:', err);
      // A superseded (stale) load must touch nothing — otherwise a stale catch
      // for thread A, firing while B is active, would see prev = B's history,
      // fail the same-session check, and BLANK B. Bail before any write.
      if (loadSeqRef.current !== loadSeq) return;
      // Same guard as the disconnect path: a failed refresh must not clobber a
      // good, same-session history (Bug C refetches every turn — a single blip
      // would otherwise blank the thread). Clear only if we were switching.
      setChatHistory((prev) =>
        prev.length > 0 && prev[0].sessionId === sessionKey ? prev : []
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  /** Batch-verify [SENDER] countersign stamps for a SHARED thread's history.
   *  Solo private threads skip the call entirely — nobody else can write into
   *  them, so there's nothing to attribute. Fire-and-forget: history renders
   *  immediately (chips show as unverified) and flips to verified when the
   *  auth server answers. */
  const verifyHistoryStamps = (sessionKey: string, mapped: ChatMessage[], loadSeq: number) => {
    const aclRow = aclRef.current.find((a) => a.sessionKey === sessionKey);
    // "Shared" = org-wide, has other members, or owned by someone else.
    // Unknown ACL (row missing / still loading) counts as shared — fail toward
    // verifying rather than silently trusting stamps we never checked.
    const isShared =
      !aclRow ||
      aclRow.owner !== username ||
      aclRow.sharedWith === '*' ||
      (Array.isArray(aclRow.sharedWith) && aclRow.sharedWith.length > 0);
    if (!isShared) return;

    const stamped = mapped.filter((m) => m.type === 'user' && m.senderInfo?.stamp);
    if (stamped.length === 0) return;

    verifyStamps(stamped.map((m) => m.senderInfo!.stamp!))
      .then((results) => {
        if (!results || loadSeqRef.current !== loadSeq) return;
        // results are index-aligned with the stamps we sent. A stamp counts as
        // verified only if the server says ok AND (when it echoes a username)
        // that username matches the one the sender claimed — a valid stamp
        // lifted from someone else's message must not launder a fake name.
        const verdictById = new Map<string, boolean>();
        stamped.forEach((m, i) => {
          const r = results[i];
          const ok = !!r?.ok && (!r.username || r.username === m.senderInfo!.username);
          verdictById.set(m.id, ok);
        });
        setChatHistory((prev) =>
          prev.map((m) =>
            verdictById.has(m.id) ? { ...m, senderVerified: verdictById.get(m.id) } : m
          )
        );
      })
      .catch(() => {
        /* fail-open: chips simply stay unverified */
      });
  };

  const createSession = useCallback(async (name?: string): Promise<string | null> => {
    if (!username) return null;
    // Owner-prefixed key — defense-in-depth signal even if ACL store is unreachable.
    const key = `${username}:thread-${Date.now().toString(36)}`;
    try {
      await createAcl(key, { name: name || undefined });
      setActiveSessionId(key);
      setChatHistory([]);
      return key;
    } catch (err) {
      console.error('[useSessions] createSession failed:', err);
      // Surface a visible error instead of silently leaving an orphan session.
      window.alert(
        `Couldn't save the new chat thread: ${(err as Error).message || 'unknown error'}\n\n` +
        'Your message will still go through, but the thread may disappear on reload. ' +
        'Try refreshing or contact support if this keeps happening.'
      );
      // Still set the session so the user isn't stuck — reconcile cron will
      // backfill the ACL within 10 minutes regardless.
      setActiveSessionId(key);
      setChatHistory([]);
      return key;
    }
  }, [username, createAcl]);

  const switchSession = useCallback((id: string | null) => {
    setActiveSessionId(id);
  }, []);

  return {
    sessions,
    activeSessionId,
    createSession,
    switchSession,
    loadingHistory,
    chatHistory,
  };
}

/** Extract user-facing session key from full internal key
 *  e.g. "agent:main:main" → "main", "agent:main:thread-abc" → "thread-abc" */
function extractSessionKey(fullKey: string): string {
  // Pattern: agent:{agentId}:{sessionKey}
  const parts = fullKey.split(':');
  if (parts.length >= 3 && parts[0] === 'agent') {
    return parts.slice(2).join(':');
  }
  return fullKey;
}

function formatSessionName(key: string): string {
  if (key === 'main') return 'Main';
  // Welcome thread: welcome:{username} → "Welcome"
  if (key.startsWith('welcome:')) return 'Welcome';
  // Owner-prefixed: {username}:thread-{ts} → "Thread {ts}"
  const ownerMatch = key.match(/^[a-z][a-z0-9_-]*:(.+)$/i);
  if (ownerMatch) {
    const tail = ownerMatch[1].replace(/^thread-/, '');
    return `Thread ${tail.slice(0, 6)}`;
  }
  // Legacy / unprefixed
  const cleaned = key.replace(/^(holler:|thread-)/, '');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/** Small deterministic string hash (djb2 → base36) for building stable,
 *  identity-based React keys without pulling in a dependency. */
function shortHash(str: string): string {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

function extractHistoryText(msg: OCHistoryMessage): string {
  if (typeof msg.content === 'string') return msg.content;
  if (Array.isArray(msg.content)) {
    return msg.content
      .filter((p) => p.type === 'text')
      .map((p) => (p as { text: string }).text)
      .join('');
  }
  return '';
}

/** Pull inline-photo refs off a history message. chat.history returns image
 *  parts with their bytes omitted ({type:'image', omitted, bytes, mimeType});
 *  `bytes` is the base64 length, which the UI passes to /api/chat/thread-image
 *  to lazy-load the actual photo. */
function extractHistoryImages(msg: OCHistoryMessage): { b64len: number; mimeType: string }[] {
  if (!Array.isArray(msg.content)) return [];
  const out: { b64len: number; mimeType: string }[] = [];
  for (const p of msg.content) {
    const part = p as { type: string; bytes?: number; mimeType?: string };
    if (part.type === 'image' && typeof part.bytes === 'number' && part.bytes > 0) {
      out.push({ b64len: part.bytes, mimeType: part.mimeType || 'image/jpeg' });
    }
  }
  return out;
}
